import { useEffect } from 'react';

import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from '~/services/common';

import { type Lens, type LensResponse, isCardLens } from '~/types/leLens';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { lensLiveStateQuery } from './lensLiveStateQuery';
import { reportingLeLensQueryKeys } from './useGetReportingLeLens';
import { refreshReportingLeLensCardMutationKey } from './useGetReportingLeLensData';
import { refreshCertifiedShelves } from './useLensCertification';

export type LensRevision = {
  summary?: string[];
  previousVersion?: number | null;
  version: number;
  actorEmail: string;
  serviceEmail?: string;
  reason: string;
  createdAt: string;
};
const path = (id: string, suffix: string) => resolveReportingLeLensUrl(`/api/reporting/le-lenses/${id}/${suffix}`);
const keys = (id: string, resource: string) => ['lens-collaboration', id, resource];

export function useLensRevisions(id: string, enabled: boolean, offset = 0) {
  return useQuery({
    queryKey: [...keys(id, 'revisions'), offset],
    enabled,
    queryFn: async () =>
      (await request(path(id, `revisions?offset=${offset}`))) as {
        revisions: Array<LensRevision>;
        currentVersion: number;
        nextOffset: number | null;
      },
    retry: false,
  });
}

export function useLensRevision(id: string, version: number | null) {
  return useQuery({
    queryKey: keys(id, `revision:${version}`),
    enabled: version !== null,
    queryFn: async () => (await request(path(id, `revisions/${version}`))) as { lens: Lens },
    retry: false,
  });
}

export function useLensCollaborationMutation(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ suffix, body }: { suffix: string; body: unknown }) =>
      (await request(path(id, suffix), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeRequestBody(body)),
      })) as { lens?: Lens },
    onSuccess: async ({ lens }) => {
      if (lens) {
        client.setQueryData<LensResponse>(reportingLeLensQueryKeys.detail(id), (current) =>
          current ? { ...current, lens } : current,
        );
      }
      await client.invalidateQueries({ queryKey: ['lens-collaboration', id] });
      if (lens) {
        await client.invalidateQueries({ queryKey: [...reportingLeLensQueryKeys.detail(id), 'data'] });
        await client.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(id) });
        await refreshCertifiedShelves(client);
      }
    },
    retry: false,
  });
}

function lensDataDefinition(lens: Lens) {
  if (!isCardLens(lens)) return JSON.stringify(lens.spec);
  return JSON.stringify({
    datasets: lens.spec.datasets,
    filters: lens.spec.filters,
    cards: lens.spec.cards.map(({ id, datasetId, view }) => ({ id, datasetId, view })),
  });
}

export function useLensLiveState(lens: Lens | undefined, editing: boolean, canEdit: boolean) {
  const client = useQueryClient();
  const refreshing = useIsMutating({ mutationKey: refreshReportingLeLensCardMutationKey }) > 0;
  const query = useQuery({
    ...lensLiveStateQuery(client, lens?.id ?? ''),
    enabled: Boolean(lens) && canEdit,
    refetchInterval: editing ? 5000 : false,
    refetchIntervalInBackground: false,
  });
  const remote = query.data;
  const refetch = query.refetch;
  useEffect(() => {
    if (!lens || !canEdit) {
      return;
    }
    const onFocus = () => void refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [canEdit, lens?.id, refetch]);
  useEffect(() => {
    if (!lens || !remote || editing || refreshing || remote.lens.version <= lens.version) {
      return;
    }
    const dataKey = [...reportingLeLensQueryKeys.detail(lens.id), 'data'];
    const dataChanged = lensDataDefinition(lens) !== lensDataDefinition(remote.lens);
    if (!dataChanged) {
      for (const [key, rows] of client.getQueriesData<{ lensVersion?: number }>({ queryKey: dataKey })) {
        const options = key[key.length - 1] as { lensVersion?: number; viewRevision?: number };
        const state = client.getQueryState(key);
        if (
          options?.lensVersion === lens.version &&
          options.viewRevision === remote.view?.revision &&
          rows?.lensVersion === lens.version &&
          state?.fetchStatus === 'idle' &&
          !state.isInvalidated
        ) {
          client.setQueryData(
            [...key.slice(0, -1), { ...options, lensVersion: remote.lens.version }],
            { ...rows, lensVersion: remote.lens.version },
            { updatedAt: state.dataUpdatedAt },
          );
        }
      }
    }
    client.setQueryData(reportingLeLensQueryKeys.detail(lens.id), remote);
    if (dataChanged) {
      client.invalidateQueries({ queryKey: dataKey, refetchType: 'none' });
    }
  }, [client, editing, lens, refreshing, remote]);
  const status = (query.error as { status?: number } | null)?.status;
  return {
    hasRemoteChanges: Boolean(lens && remote && remote.lens.version > lens.version),
    accessError: status && [401, 403, 404].includes(status) ? query.error : null,
  };
}
